import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TrendingUp, TrendingDown, BookOpen, Trophy, ArrowRight } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { Activity } from "lucide-react";
import FeatureHint from "@/components/FeatureHint";

/**
 * RecentActivityFeed — pinned card on the Dashboard.
 *
 * Merges last 3 closed trades + last 2 completed lessons + latest leaderboard
 * rank change from GET /api/activity/recent. Each row deep-links to the
 * relevant page.
 */
export default function RecentActivityFeed() {
    const [events, setEvents] = useState(null);

    async function load() {
        try {
            const { data } = await api.get("/activity/recent");
            setEvents(data.events || []);
        } catch { setEvents([]); }
    }

    useEffect(() => {
        load();
        const id = setInterval(load, 60000);
        return () => clearInterval(id);
    }, []);

    if (events === null) {
        return (
            <div className="border border-border bg-card rounded-lg p-4" data-testid="recent-activity-loading">
                <div className="overline mb-3">Recent activity</div>
                <div className="space-y-2">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="h-12 rounded bg-secondary/50 animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="border border-border bg-card rounded-lg p-4" data-testid="recent-activity-feed">
            <div className="flex items-center justify-between mb-3">
                <div className="overline flex items-center gap-1.5">
                    Recent activity
                    <FeatureHint
                        title="Recent activity"
                        body="Your last few closed trades, completed lessons, and leaderboard rank changes. Tap any row to jump straight to the relevant page."
                        bullets={[
                            "Auto-refreshes every 60s.",
                            "Shows the last 3 trades + 2 lessons + 1 rank change.",
                        ]}
                        testId="hint-recent-activity"
                    />
                </div>
                {events.length > 0 && (
                    <Link
                        to="/app/terminal"
                        className="text-[11px] text-muted-foreground hover:text-primary"
                        data-testid="recent-activity-view-all"
                    >
                        View all
                    </Link>
                )}
            </div>
            {events.length === 0 ? (
                <EmptyState
                    icon={Activity}
                    title="No activity yet"
                    body="Place your first virtual trade or complete a lesson — this feed lights up in real-time."
                    ctaLabel="Open the Terminal"
                    ctaTo="/app/terminal"
                    ctaTestId="recent-activity-cta-terminal"
                    testId="recent-activity-empty"
                    dense
                />
            ) : (
                <ul className="space-y-2">
                    {events.map((ev, idx) => (
                        <ActivityRow key={`${ev.type}-${idx}-${ev.ts}`} ev={ev} />
                    ))}
                </ul>
            )}
        </div>
    );
}

function ActivityRow({ ev }) {
    const Icon = pickIcon(ev);
    const toneClass =
        ev.tone === "up" ? "text-success bg-success/10"
            : ev.tone === "down" ? "text-danger bg-danger/10"
            : ev.tone === "success" ? "text-emerald-600 bg-emerald-500/10"
            : "text-primary bg-primary/10";

    return (
        <li>
            <Link
                to={ev.cta_route || "#"}
                data-testid={`recent-activity-row-${ev.type}`}
                className="group flex items-center gap-3 rounded-md border border-transparent hover:border-border hover:bg-secondary/40 px-2 py-2 -mx-2 transition-colors"
            >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${toneClass}`}>
                    <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold truncate">{ev.title}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                        <span className="truncate">{ev.detail}</span>
                        <span className="opacity-60">·</span>
                        <span className="mono opacity-70 shrink-0">{fmtRel(ev.ts)}</span>
                    </div>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
        </li>
    );
}

function pickIcon(ev) {
    if (ev.type === "trade") return ev.tone === "up" ? TrendingUp : TrendingDown;
    if (ev.type === "lesson") return BookOpen;
    return Trophy;
}

function fmtRel(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (!then) return "";
    const diff = (Date.now() - then) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString("en-KE", { month: "short", day: "numeric" });
}
